import { useEffect, useMemo, useState } from "react";
import { useTranslate } from "../i18n/useTranslate";

const REFRESH_INTERVAL_MS = 30 * 60 * 1000;

function describeWeather(code) {
  if (code == null) return "eventDetails.weather.condition.notAvailable";
  const c = Number(code);
  if (c === 0) return "eventDetails.weather.condition.clear";
  if ([1, 2, 3].includes(c)) return "eventDetails.weather.condition.partlyCloudy";
  if ([45, 48].includes(c)) return "eventDetails.weather.condition.fog";
  if ([51, 53, 55, 56, 57].includes(c)) return "eventDetails.weather.condition.drizzle";
  if ([61, 63, 65].includes(c)) return "eventDetails.weather.condition.rain";
  if ([66, 67].includes(c)) return "eventDetails.weather.condition.freezingRain";
  if ([71, 73, 75, 77].includes(c)) return "eventDetails.weather.condition.snow";
  if ([80, 81, 82].includes(c)) return "eventDetails.weather.condition.showers";
  if ([85, 86].includes(c)) return "eventDetails.weather.condition.snowShowers";
  if ([95, 96, 99].includes(c)) return "eventDetails.weather.condition.thunderstorm";
  return "eventDetails.weather.condition.notAvailable";
}

function weatherSymbol(code) {
  const c = Number(code);
  if (c === 0) return "☀️";
  if ([1, 2].includes(c)) return "🌤️";
  if (c === 3) return "☁️";
  if ([45, 48].includes(c)) return "🌫️";
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(c)) {
    return "🌧️";
  }
  if ([71, 73, 75, 77, 85, 86].includes(c)) return "🌨️";
  if ([95, 96, 99].includes(c)) return "⛈️";
  return "🌡️";
}

function formatHour(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--:--";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function normalizeHourlyForecast(hourly) {
  const times = Array.isArray(hourly?.time) ? hourly.time : [];
  const now = Date.now();
  const firstUpcoming = times.findIndex((time) => new Date(time).getTime() >= now);
  const start = firstUpcoming >= 0 ? firstUpcoming : 0;

  return times.slice(start, start + 3).map((time, offset) => {
    const index = start + offset;
    return {
      time,
      temperature: hourly?.temperature_2m?.[index] ?? null,
      precipitation: hourly?.precipitation_probability?.[index] ?? null,
      code: hourly?.weather_code?.[index] ?? null,
      windKmh: hourly?.wind_speed_10m?.[index] ?? null,
    };
  });
}

export default function ActiveWeatherForecast({ position }) {
  const translate = useTranslate();
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const locationKey = useMemo(() => {
    const lat = Number(position?.lat ?? position?.latitude);
    const lon = Number(position?.lon ?? position?.lng ?? position?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    return { lat, lon, key: `${lat.toFixed(2)}|${lon.toFixed(2)}` };
  }, [position?.lat, position?.latitude, position?.lon, position?.lng, position?.longitude]);

  useEffect(() => {
    if (!locationKey) {
      setForecast([]);
      return undefined;
    }

    let cancelled = false;
    let controller = null;

    const load = async () => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        if (!cancelled) setError(true);
        return;
      }

      controller?.abort();
      controller = new AbortController();
      try {
        setLoading(true);
        setError(false);
        const params = new URLSearchParams({
          latitude: String(locationKey.lat),
          longitude: String(locationKey.lon),
          hourly:
            "temperature_2m,precipitation_probability,weather_code,wind_speed_10m",
          forecast_hours: "6",
          timezone: "auto",
        });
        const response = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Weather HTTP ${response.status}`);
        const data = await response.json();
        if (!cancelled) setForecast(normalizeHourlyForecast(data?.hourly));
      } catch (err) {
        if (!cancelled && err?.name !== "AbortError") setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const interval = window.setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      controller?.abort();
      window.clearInterval(interval);
    };
  }, [locationKey?.key]);

  return (
    <section
      className="tk-active-weather"
      style={{
        padding: "9px 10px 10px",
        borderRadius: 10,
        background: "rgba(255,255,255,0.9)",
        border: "1px solid rgba(0,0,0,0.06)",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: "#4b5563", marginBottom: 7 }}>
        {translate("nav.weather.nextHours")}
      </div>

      {loading && forecast.length === 0 && (
        <div style={{ fontSize: 12, color: "#555" }}>
          {translate("eventDetails.weather.loading")}
        </div>
      )}

      {(!locationKey || (error && forecast.length === 0) || (!loading && forecast.length === 0)) && (
        <div style={{ fontSize: 12, color: error ? "#b00000" : "#555" }}>
          {translate(
            !locationKey ? "nav.weather.waitingLocation" : "eventDetails.weather.notAvailable"
          )}
        </div>
      )}

      {forecast.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 6 }}>
          {forecast.map((hour) => (
            <div
              key={hour.time}
              className="tk-active-weather-card"
              style={{
                minWidth: 0,
                padding: "7px 4px",
                borderRadius: 9,
                background: "rgba(248,250,249,0.96)",
                border: "1px solid rgba(40,167,69,0.16)",
                textAlign: "center",
              }}
            >
              <div className="tk-active-weather-time" style={{ fontSize: 12, fontWeight: 700, color: "#333" }}>
                {formatHour(hour.time)}
              </div>
              <div style={{ fontSize: 20, lineHeight: 1.35 }}>{weatherSymbol(hour.code)}</div>
              <div className="tk-active-weather-temperature" style={{ fontSize: 16, fontWeight: 700, color: "#28a745" }}>
                {hour.temperature != null
                  ? `${Math.round(hour.temperature)}°C`
                  : translate("eventDetails.weather.na")}
              </div>
              <div
                className="tk-active-weather-description"
                style={{
                  fontSize: 10,
                  color: "#4b5563",
                  lineHeight: 1.2,
                  minHeight: 24,
                  overflowWrap: "anywhere",
                }}
              >
                {translate(describeWeather(hour.code))}
              </div>
              <div className="tk-active-weather-meta" style={{ fontSize: 10, color: "#555", marginTop: 3 }}>
                💧 {hour.precipitation != null ? `${Math.round(hour.precipitation)}%` : "—"}
                {" · "}
                💨 {hour.windKmh != null ? `${Math.round(hour.windKmh)} km/h` : "—"}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
